import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { SegmentedTabs } from '@/components/ui/SegmentedTabs'
import { getDealerMetrics } from '@/services/dealer.service'
import { toast } from '@/utils/helpers'

/**
 * Received volume over time — the trend half of WARP-943.
 *
 * Bars, because the measure is a count per discrete period and the job is comparing
 * magnitudes between periods. One series, so no legend: the heading says what is
 * plotted.
 *
 * It reads `ro_volume` with `date_field=received_at`. That matters — the matrix above
 * buckets on received_at, and a trend counting a different date would not add up to
 * the Received row for the same range.
 */

// Blue-800, the app's primary. 7.67:1 on the card surface.
const BAR = '#045485'

const RANGES = [
  { id: '4w', label: '4W', days: 28, groupBy: 'day' },
  { id: '3m', label: '3M', days: 91, groupBy: 'week' },
  { id: '6m', label: '6M', days: 182, groupBy: 'week' },
  { id: 'ytd', label: 'YTD', groupBy: 'week' },
  { id: '1y', label: '1Y', days: 365, groupBy: 'month' },
]

const GRANULARITIES = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
]

const VIEW_BOX = { width: 840, height: 220 }
const PADDING = { top: 16, right: 12, bottom: 28, left: 44 }
const MAX_BAR_WIDTH = 24
const SURFACE_GAP = 2
const BAR_RADIUS = 4

const rangeBounds = (range) => {
  const end = dayjs()
  if (range.id === 'ytd') return [end.startOf('year'), end]
  return [end.subtract(range.days - 1, 'day'), end]
}

/**
 * Round the axis top to something a person would say out loud, and keep it even so
 * the midpoint tick is a whole number too.
 */
const niceCeiling = (value) => {
  if (value <= 2) return 2
  if (value <= 10) return Math.ceil(value / 2) * 2
  const magnitude = 10 ** Math.floor(Math.log10(value))
  return Math.ceil(value / magnitude) * magnitude
}

const formatTick = (period, groupBy) => {
  const date = dayjs(period)
  if (groupBy === 'month') return date.format('MMM')
  return date.format('MMM D')
}

const formatPeriod = (period, groupBy) => {
  const date = dayjs(period)
  if (groupBy === 'month') return date.format('MMM YYYY')
  if (groupBy === 'week') return `Week of ${date.format('MMM D')}`
  return date.format('MMM D, YYYY')
}

/** A bar with rounded data-end and square feet on the baseline. */
const barPath = (x, y, width, height) => {
  const r = Math.min(BAR_RADIUS, width / 2, height)
  if (height <= 0) return ''
  return [
    `M ${x} ${y + height}`,
    `L ${x} ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`,
    `L ${x + width - r} ${y}`,
    `Q ${x + width} ${y} ${x + width} ${y + r}`,
    `L ${x + width} ${y + height}`,
    'Z',
  ].join(' ')
}

const VolumeTrend = ({ dealerId }) => {
  const [rangeId, setRangeId] = useState('3m')
  const [granularity, setGranularity] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [hovered, setHovered] = useState(null)
  const abortRef = useRef(null)

  const range = RANGES.find((option) => option.id === rangeId)
  // Granularity follows the range until the reader overrides it, per FR-19.
  const groupBy = granularity || range.groupBy

  useEffect(() => {
    if (!dealerId) return undefined

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    const [from, to] = rangeBounds(range)
    setLoading(true)
    getDealerMetrics(
      dealerId,
      {
        metric: 'ro_volume',
        group_by: groupBy,
        date_field: 'received_at',
        start: from.format('YYYY-MM-DD'),
        end: to.format('YYYY-MM-DD'),
      },
      () => {},
      (message) => toast(message, 'error'),
    ).then((response) => {
      if (controller.signal.aborted) return
      setLoading(false)
      setRows(response?.data || [])
    })

    return () => controller.abort()
  }, [dealerId, rangeId, groupBy])

  const chart = useMemo(() => {
    const innerWidth = VIEW_BOX.width - PADDING.left - PADDING.right
    const innerHeight = VIEW_BOX.height - PADDING.top - PADDING.bottom
    const max = Math.max(...rows.map((row) => row.value), 0)
    const ceiling = niceCeiling(max)
    const slot = rows.length ? innerWidth / rows.length : innerWidth
    const barWidth = Math.max(1, Math.min(MAX_BAR_WIDTH, slot - SURFACE_GAP))
    const peakIndex = rows.reduce(
      (best, row, index) => (row.value > (rows[best]?.value ?? -1) ? index : best),
      0,
    )

    return {
      innerWidth,
      innerHeight,
      ceiling,
      slot,
      barWidth,
      peakIndex,
      total: rows.reduce((sum, row) => sum + row.value, 0),
      bars: rows.map((row, index) => {
        const height = ceiling ? (row.value / ceiling) * innerHeight : 0
        return {
          ...row,
          index,
          x: PADDING.left + index * slot + (slot - barWidth) / 2,
          y: PADDING.top + innerHeight - height,
          height,
          // The period we are standing in is not finished, so it is drawn lighter —
          // otherwise the last bar always reads as a drop.
          isPartial: index === rows.length - 1,
        }
      }),
    }
  }, [rows])

  const ticks = [0, chart.ceiling / 2, chart.ceiling]
  // At most ~9 date labels, always including the first and the last, so the axis
  // never turns into a smear of overlapping text.
  const tickEvery = Math.max(1, Math.ceil(chart.bars.length / 9))
  const active = hovered === null ? null : chart.bars[hovered]

  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-charcoal-900 text-lg font-semibold">Received volume</h2>
          <p className="text-charcoal-500 mt-0.5 text-xs">
            {loading
              ? 'Loading…'
              : `${chart.total.toLocaleString()} repair orders received, by date received`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SegmentedTabs
            activeTab={groupBy}
            ariaLabel="Granularity"
            onChange={setGranularity}
            panelId="volume-trend-chart"
            tabs={GRANULARITIES}
          />

          <div className="bg-charcoal-100 inline-flex rounded-lg p-1" role="group">
            {RANGES.map((option) => (
              <button
                aria-pressed={option.id === rangeId}
                className={mergeClassNames(
                  'focus-visible:ring-blue rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-all focus-visible:outline-none focus-visible:ring-2',
                  option.id === rangeId
                    ? 'bg-white text-blue-800 shadow-sm'
                    : 'text-charcoal-400 hover:text-charcoal-900',
                )}
                key={option.id}
                onClick={() => {
                  setRangeId(option.id)
                  // A new range brings its own sensible granularity back.
                  setGranularity(null)
                }}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative" id="volume-trend-chart">
        {rows.length === 0 && !loading ? (
          <p className="text-charcoal-400 py-16 text-center text-sm">
            No repair orders received in this range.
          </p>
        ) : (
          <>
            <svg
              aria-label="Received repair orders per period"
              className={mergeClassNames('h-auto w-full', loading && 'opacity-40')}
              role="img"
              viewBox={`0 0 ${VIEW_BOX.width} ${VIEW_BOX.height}`}
            >
              {ticks.map((tick) => {
                const y =
                  PADDING.top + chart.innerHeight - (tick / chart.ceiling) * chart.innerHeight
                return (
                  <g key={tick}>
                    <line
                      stroke="#e6e6e7"
                      strokeWidth="1"
                      x1={PADDING.left}
                      x2={VIEW_BOX.width - PADDING.right}
                      y1={y}
                      y2={y}
                    />
                    <text
                      className="fill-charcoal-400 text-[11px] tabular-nums"
                      textAnchor="end"
                      x={PADDING.left - 8}
                      y={y + 4}
                    >
                      {Math.round(tick).toLocaleString()}
                    </text>
                  </g>
                )
              })}

              {chart.bars.map((bar) => (
                <path
                  d={barPath(bar.x, bar.y, chart.barWidth, bar.height)}
                  fill={BAR}
                  fillOpacity={bar.isPartial ? 0.45 : 1}
                  key={bar.period}
                />
              ))}

              {/* Label the peak only. A number on every bar goes unread. */}
              {chart.bars[chart.peakIndex]?.value > 0 && (
                <text
                  className="fill-charcoal-700 text-[11px] font-semibold tabular-nums"
                  textAnchor="middle"
                  x={chart.bars[chart.peakIndex].x + chart.barWidth / 2}
                  y={chart.bars[chart.peakIndex].y - 5}
                >
                  {chart.bars[chart.peakIndex].value.toLocaleString()}
                </text>
              )}

              {chart.bars.map((bar, index) => {
                const isFirst = index === 0
                const isLast = index === chart.bars.length - 1
                if (!isFirst && !isLast && index % tickEvery !== 0) return null

                // The end labels hug the plot edges so they never run off the card.
                let anchor = 'middle'
                let x = bar.x + chart.barWidth / 2
                if (isFirst) {
                  anchor = 'start'
                  x = PADDING.left
                } else if (isLast) {
                  anchor = 'end'
                  x = VIEW_BOX.width - PADDING.right
                }

                return (
                  <text
                    className="fill-charcoal-400 text-[11px]"
                    key={`tick-${bar.period}`}
                    textAnchor={anchor}
                    x={x}
                    y={VIEW_BOX.height - 8}
                  >
                    {formatTick(bar.period, groupBy)}
                  </text>
                )
              })}

              {/* Hit targets are the full slot, not the bar — a 4px bar is unhoverable. */}
              {chart.bars.map((bar) => (
                <rect
                  fill="transparent"
                  height={chart.innerHeight + PADDING.top}
                  key={`hit-${bar.period}`}
                  onMouseEnter={() => setHovered(bar.index)}
                  onMouseLeave={() => setHovered(null)}
                  width={chart.slot}
                  x={PADDING.left + bar.index * chart.slot}
                  y={0}
                />
              ))}
            </svg>

            {active && (
              <div
                className="border-charcoal-200 pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-lg border bg-white px-3 py-2 shadow-md"
                style={{
                  left: `${
                    ((PADDING.left + active.index * chart.slot + chart.slot / 2) /
                      VIEW_BOX.width) *
                    100
                  }%`,
                }}
              >
                <p className="text-charcoal-500 whitespace-nowrap text-[11px]">
                  {formatPeriod(active.period, groupBy)}
                </p>
                <p className="text-charcoal-900 text-sm font-semibold tabular-nums">
                  {active.value.toLocaleString()} received
                </p>
                {active.isPartial && (
                  <p className="text-charcoal-400 text-[11px]">Period still in progress</p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {rows.length > 0 && (
        <details className="mt-3">
          <summary className="text-charcoal-500 hover:text-charcoal-900 cursor-pointer text-xs">
            Show as table
          </summary>
          <table className="mt-2 w-full text-sm">
            <thead className="text-charcoal-700 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="py-1 text-left font-bold">Period</th>
                <th className="py-1 text-right font-bold">Received</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr className="border-charcoal-100 border-t" key={row.period}>
                  <td className="text-charcoal-700 py-1">{formatPeriod(row.period, groupBy)}</td>
                  <td className="text-charcoal-900 py-1 text-right tabular-nums">
                    {row.value.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  )
}

VolumeTrend.propTypes = {
  dealerId: PropTypes.string,
}

export default VolumeTrend
